import {
  ADD_SEGMENTS,
  ADD_UPLOAD_FILE,
  ADD_VARIABLE_GROUP,
  BUILD_AUDIENCE,
  CHANGE_BUILD_MODE,
  CHANGE_DESCRIPTION,
  CHANGE_GROUP_OPERATOR,
  CHANGE_GROUP_VARIABLES,
  CHANGE_INSIGHT_META,
  CHANGE_NAME,
  CHANGE_OPERATOR,
  CHANGE_SEGMENT,
  CHANGE_TRAVELTIME,
  CHANGE_UPLOAD_MODE,
  CHECK_UPLOAD_FILE,
  CLEAR_UPLOAD_FILE,
  CREATE_ACTIVATION,
  CREATE_AUDIENCE,
  CREATE_INSIGHT,
  DELETE_ACTIVATION,
  DELETE_AUDIENCE,
  ERROR,
  GET_ACTIVATION,
  GET_ACTIVATION_LIST,
  GET_AUDIENCE,
  GET_AUDIENCE_LIST,
  LOADING,
  REMOVE_VARIABLE_GROUP,
  RESET,
  RESET_AUDIENCE_STATUS,
  RESET_VARIABLE_GROUP,
  SET_GEO_FENCE,
  SET_STATUS,
  SHARE_ACTIVATION,
  SHARE_AUDIENCE,
  UPDATE_ACTIVATION,
  UPDATE_AUDIENCE,
} from "./AudienceActions";
import {
  addQueryGroup,
  addUploadFile,
  changeBuildMode,
  changeDescription,
  changeName,
  changeOperator,
  changeTravelTime,
  changeUploadMode,
  changeVariables,
  createDefinition,
  removeQueryGroup,
  resetQueryGroup,
  setGeoFence,
  setStatus,
} from "./helpers";
import { Action, AudienceState } from "./types";

export const audienceReducer = (
  state: AudienceState,
  action: Action
): AudienceState => {
  switch (action.type) {
    case CREATE_AUDIENCE:
      return {
        ...state,
        currentAudience: null,
        currentActivation: null,
        currentSegment: null,
        insight: null,
        loading: false,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case GET_AUDIENCE_LIST:
      return {
        ...state,
        audiences: action.payload,
        currentAudience: null,
        currentSegment: null,
        insight: null,
        loading: false,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case GET_AUDIENCE:
      return {
        ...state,
        currentActivation: null,
        currentAudience: action.payload.audience,
        currentDefinition: createDefinition(action.payload.audience),
        currentSegment: null,
        insight: null,
        loading: false,
        change: false,
        built: action.payload.built ?? true,
        saved: true,
        error: null,
      };

    case RESET_AUDIENCE_STATUS:
      return {
        ...state,
        loading: false,
        validFile: null,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case BUILD_AUDIENCE:
      return {
        ...state,
        currentAudience: state.currentAudience
          ? {
              ...state.currentAudience,
              audience_id: action.payload.audience_id ?? "",
              audience_geo: action.payload.audience_geo,
              audience_size: action.payload.audience_size,
            }
          : state.currentAudience,
        currentSegment: null,
        insight: null,
        loading: false,
        built: true,
        change: true,
        saved: false,
        error: null,
      };

    case ADD_SEGMENTS:
      return {
        ...state,
        currentAudience: state.currentAudience
          ? {
              ...state.currentAudience,
              segment: true,
              segments: action.payload,
            }
          : state.currentAudience,
      };

    case CHANGE_SEGMENT:
      return {
        ...state,
        currentSegment: action.payload,
      };

    case CREATE_INSIGHT: {
      return {
        ...state,
        currentAudience: action.payload.audience,
        insight: action.payload.insight,
        loading: false,
        change: false,
        error: null,
      };
    }

    case UPDATE_AUDIENCE: {
      return {
        ...state,
        audiences: action.payload.audiences,
        currentAudience: action.payload.currentAudience,
        currentDefinition: createDefinition(action.payload.currentAudience),
        insight: null,
        loading: false,
        change: false,
        saved: true,
        error: null,
      };
    }

    case DELETE_AUDIENCE: {
      return {
        ...state,
        change: false,
        audiences: action.payload,
      };
    }

    case SHARE_ACTIVATION: {
      const { activationId, userIds } = action.payload;
      return {
        ...state,
        currentActivation: state.currentActivation
          ? {
              ...state.currentActivation,
              shared_with: userIds.length > 0 ? userIds : [],
            }
          : state.currentActivation,
        activations: state.activations
          ? state.activations.map((item) => {
              if (item.campaign_id === activationId) {
                if (userIds.length === 0) {
                  return { ...item, shared_with: [] };
                }
                return { ...item, shared_with: userIds };
              }
              return item;
            })
          : state.activations,
      };
    }

    case SHARE_AUDIENCE: {
      const { audienceId, userIds } = action.payload;
      return {
        ...state,
        currentAudience: state.currentAudience
          ? {
              ...state.currentAudience,
              shared_with: userIds.length > 0 ? userIds : [],
            }
          : state.currentAudience,
        audiences: state.audiences
          ? state.audiences.map((item) => {
              if (item.audience_id === audienceId) {
                if (userIds.length === 0) {
                  return { ...item, shared_with: [] };
                }
                return { ...item, shared_with: userIds };
              }
              return item;
            })
          : state.audiences,
      };
    }

    case RESET:
      return {
        ...state,
        currentAudience: null,
        currentDefinition: '',
        currentSegment: null,
        currentActivation: null,
        insight: null,
        loading: false,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case LOADING:
      return {
        ...state,
        loading: true,
        change: false,
        error: null,
      };

    case ERROR:
      return {
        ...state,
        loading: false,
        change: false,
        error: action.payload,
      };

    case ADD_VARIABLE_GROUP:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? addQueryGroup(state.currentAudience)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case REMOVE_VARIABLE_GROUP:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? removeQueryGroup(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case RESET_VARIABLE_GROUP:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? resetQueryGroup(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CHANGE_OPERATOR:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? changeOperator(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CHANGE_BUILD_MODE:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? changeBuildMode(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CHANGE_GROUP_OPERATOR:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? changeOperator(
                state.currentAudience,
                action.payload.operator,
                action.payload.id
              )
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CHANGE_GROUP_VARIABLES:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? changeVariables(
                state.currentAudience,
                action.payload.id,
                action.payload.variables
              )
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case SET_STATUS:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          setStatus(state.currentAudience, action.payload),
        change: true,
        built: false,
        saved: false,
      };

    case ADD_UPLOAD_FILE:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "upload"
            ? addUploadFile(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CLEAR_UPLOAD_FILE:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "upload"
            ? addUploadFile(state.currentAudience, null)
            : state.currentAudience,
      }

    case CHECK_UPLOAD_FILE:
      return {
        ...state,
        validFile: action.payload
      };

    case CHANGE_UPLOAD_MODE:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "upload"
            ? changeUploadMode(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case SET_GEO_FENCE:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "build"
            ? setGeoFence(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    case CHANGE_NAME:
      return {
        ...state,
        currentAudience: state.currentAudience
          ? changeName(
              state.currentAudience,
              action.payload.audienceName,
              action.payload.projectName
            )
          : state.currentAudience,
        audiences: state.audiences
          ? state.audiences.map((audience) => {
              if (audience.audience_id === action.payload.audienceId) {
                return changeName(
                  audience,
                  action.payload.audienceName,
                  action.payload.projectName
                );
              } else {
                return audience;
              }
            })
          : state.audiences,
      };

    case CREATE_ACTIVATION:
      return {
        ...state,
        currentAudience: null,
        currentActivation: null,
        currentSegment: null,
        insight: null,
        loading: false,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case GET_ACTIVATION_LIST:
      return {
        ...state,
        activations: action.payload,
        loading: false,
      };

    case GET_ACTIVATION:
      return {
        ...state,
        currentActivation: action.payload,
        currentAudience: null,
        currentSegment: null,
        insight: null,
        loading: false,
        change: false,
        built: false,
        saved: false,
        error: null,
      };

    case UPDATE_ACTIVATION: {
      return {
        ...state,
        currentActivation: action.payload,
      };
    }

    case DELETE_ACTIVATION: {
      return {
        ...state,
        change: false,
        activations: action.payload,
      };
    }

    case CHANGE_DESCRIPTION:
      return {
        ...state,
        insight:
          state.insight &&
          changeDescription(
            state.insight,
            action.payload.segmentNum,
            action.payload.description
          ),
      };

    case CHANGE_INSIGHT_META:
      return {
        ...state,
        insight: state.insight && { ...state.insight, meta: action.payload },
      };

    case CHANGE_TRAVELTIME:
      return {
        ...state,
        currentAudience:
          state.currentAudience &&
          state.currentAudience.audience_type === "upload"
            ? changeTravelTime(state.currentAudience, action.payload)
            : state.currentAudience,
        change: true,
        built: false,
        saved: false,
      };

    default:
      return state;
  }

};
